import type { useToast } from "@chakra-ui/react";
import type { GuidedCreateCampaignParams } from "../guidedFlow/types";
import actions from "src/actions";
import {
  mapGuidedTargetingToLaunchWire,
  wireFormBudgetToLaunchCampaignWire,
  wireFormScheduleToLaunchSchedule,
} from "./guidedCampaignLaunchWire";
import { mapCreativeBlockToGuidedCreative } from "./mapCreativeBlockToGuidedCreative";

export type LaunchCampaignParams = GuidedCreateCampaignParams;
export type LaunchCampaignResult = {
  campaignId: string;
  publishUpdateId?: string | null;
  publishAttempted?: boolean;
};

export function buildLaunchCampaignPayload(params: LaunchCampaignParams) {
  const apiCreatives = params.creatives.map((c) => {
    const wire = mapCreativeBlockToGuidedCreative(c, {
      partnerPostsEnabled: params.partnerPostsEnabled ?? false,
    });
    return {
      ...wire,
      fb_page_name: c.fbPage?.name ?? "",
      instagram_name: c.instagramAccount?.username ?? "",
    };
  });
  return {
    event_id: params.event_id,
    ad_account_id: params.ad_account_id,
    budget: wireFormBudgetToLaunchCampaignWire(
      params.budget,
      params.eventTimezone
    ),
    schedule: wireFormScheduleToLaunchSchedule(
      params.schedule,
      params.eventTimezone
    ),
    creatives: apiCreatives,
    targeting: mapGuidedTargetingToLaunchWire(params.targeting),
  };
}

export async function launchCampaignWithPolling(
  params: LaunchCampaignParams,
  deps: {
    toast: ReturnType<typeof useToast>;
    pollForResult: (args: {
      campaign_id: string;
      update_id: string;
      title: string;
      description: string;
      onStatusUpdate?: (status: string | null) => void;
      onDone?: (success: boolean) => void;
    }) => Promise<void>;
    onStatusUpdate?: (status: string | null) => void;
    onDone?: (success: boolean) => void;
    awaitPolling?: boolean;
  }
) {
  const payload = buildLaunchCampaignPayload(params);
  const response = (await actions.ADMIN.launchCampaign(payload)) as {
    campaign_id?: string;
    publish?: {
      attempted?: boolean;
      succeeded?: boolean;
      error?: string | null;
      update_id?: string | null;
    };
  };
  if (!response?.campaign_id) {
    return null as LaunchCampaignResult | null;
  }
  const result: LaunchCampaignResult = {
    campaignId: response.campaign_id,
    publishUpdateId: response.publish?.update_id ?? null,
    publishAttempted: Boolean(response.publish?.attempted),
  };
  if (
    response.publish?.attempted &&
    response.publish.succeeded === false
  ) {
    const description =
      response.publish.error?.trim() ||
      "The campaign was saved, but activation did not start.";
    deps.toast({
      title: "Activation did not start",
      description,
      status: "error",
    });
  }
  if (
    response?.publish?.attempted &&
    response?.campaign_id &&
    response?.publish?.update_id &&
    (deps.awaitPolling ?? true)
  ) {
    await deps.pollForResult({
      campaign_id: response.campaign_id,
      update_id: response.publish.update_id,
      title: "Campaign published",
      description: "Campaign published",
      onStatusUpdate: deps.onStatusUpdate,
      onDone: deps.onDone
        ? (success: boolean) => deps.onDone?.(success)
        : undefined,
    });
  }
  return result;
}
